import { useState } from "react";
import { Phone, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TIMES = ["Maintenant", "Dans la journée", "Demain matin", "Demain après-midi", "Cette semaine"];

export default function FloatingActions() {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({ first_name: "", phone: "", preferred_time: "", message: "", consent: false });
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.first_name.trim() || form.phone.trim().length < 6) { setError("Prénom et téléphone valides requis."); return; }
        if (!form.consent) { setError("Acceptez la politique de confidentialité."); return; }
        setSubmitting(true);
        try { await api.post("/callbacks", form); setDone(true); toast.success("Nous vous rappelons sous peu."); }
        catch (err) { setError(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    const close = () => {
        setOpen(false);
        setTimeout(() => {
            setDone(false);
            setForm({ first_name: "", phone: "", preferred_time: "", message: "", consent: false });
            setError(null);
        }, 200);
    };

    return (
        <>
            <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-3" data-testid="floating-actions">
                <button onClick={() => setOpen(true)} data-testid="floating-callback"
                    className="w-14 h-14 rounded-full bg-brand-sage hover:bg-brand-sageDark text-brand-bg flex items-center justify-center shadow-lg transition-transform hover:scale-105"
                    aria-label="Demander un rappel">
                    <Phone size={22} strokeWidth={1.5} />
                </button>
            </div>
            {open && (
                <div className="fixed inset-0 z-50 bg-brand-ink/60 flex items-center justify-center px-6" data-testid="callback-modal">
                    <div className="bg-brand-bg max-w-md w-full p-8 md:p-10 border border-brand-border relative">
                        <button onClick={close} className="absolute top-4 right-4 text-brand-sageDark hover:text-brand-ink" data-testid="callback-close" aria-label="Fermer">
                            <X size={20} />
                        </button>
                        {done ? (
                            <div className="text-center py-6">
                                <Phone size={32} className="text-brand-sage mx-auto mb-5" strokeWidth={1.5} />
                                <h2 className="font-serif text-2xl text-brand-ink font-light">Demande bien reçue</h2>
                                <p className="mt-3 text-brand-sageDark font-light leading-relaxed">Un membre de Japet Agencement vous rappelle dans le créneau souhaité.</p>
                                <button onClick={close} className="mt-7 text-xs uppercase tracking-[0.22em] text-brand-sage border-b border-brand-sage pb-1">Fermer</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-5">
                                <div>
                                    <span className="overline">Rappel rapide</span>
                                    <h2 className="mt-3 font-serif text-3xl text-brand-ink font-light">On vous rappelle.</h2>
                                    <p className="mt-2 text-sm text-brand-sageDark">Laissez vos coordonnées, nous vous appelons sous 24h ouvrées.</p>
                                </div>
                                <div>
                                    <label className="overline block mb-2">Prénom *</label>
                                    <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="field-input" data-testid="callback-first-name" autoComplete="given-name" required />
                                </div>
                                <div>
                                    <label className="overline block mb-2">Téléphone *</label>
                                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field-input" data-testid="callback-phone" autoComplete="tel" required />
                                </div>
                                <div>
                                    <label className="overline block mb-2">Créneau souhaité</label>
                                    <div className="flex flex-wrap gap-2">
                                        {TIMES.map((t) => (
                                            <button type="button" key={t} onClick={() => setForm({ ...form, preferred_time: t })}
                                                className={`px-3 py-1.5 text-xs border transition-colors ${form.preferred_time === t ? "bg-brand-sage text-brand-bg border-brand-sage" : "border-brand-sage/40 text-brand-sageDark hover:border-brand-sage"}`}
                                                data-testid={`callback-time-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <label className="flex items-start gap-2 text-xs text-brand-sageDark cursor-pointer">
                                    <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-brand-sage" data-testid="callback-consent" />
                                    <span>J'accepte d'être recontacté(e) par Japet Agencement.</span>
                                </label>
                                {error && <p className="text-sm text-red-700">{error}</p>}
                                <button type="submit" disabled={submitting} data-testid="callback-submit" className="w-full py-3.5 bg-brand-ink hover:bg-brand-sage text-brand-bg text-xs uppercase tracking-[0.22em] transition-colors disabled:opacity-50">
                                    {submitting ? "Envoi…" : "Être rappelé(e)"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
